import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Autocomplete, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useGetconsummasterQuery } from '../../../features/consummaster/consummasterApiSlice';
import { useGetpartnumberQuery } from '../../../features/partnumber/partnumberApiSlice';
import { useGetModelNumbersQuery } from '../../../features/modelnumber/modelNumberApiSlice';
import { useGetStoreOfficesQuery } from '../../../features/storeoffices/storeofficesApiSlice';
import { useGetconsumfinalQuery } from '../../../features/consumfinal/consumfinalApiSlice';
import FilteredAssetsFetcher from '../../Assets/Myasset/FilteredAssetsFetcher';
import useAuth from '../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';

// Styled components
const Addconsumformcont = styled.div`
  display: flex;
  margin-left: 10px;
`;

const LabelInputWrapper = styled.div`
  margin-bottom: 5px;
  min-height: 60px;
  margin-right: 20px;
`;

const UnivConsumSelector = ({
  addSpecIndex,
  setAddSpecIndex,
  setSelectedConsum,
  selectedConsum,
  storeSelectedValue,
  hidePartNumber = false
}) => {
  const [allData, setAllData] = useState([]);
  const [filters, setFilters] = useState({
    perm_cur_store: [],
    perm_alloc_ofc: [],
    perm_alloc_user: [],
    perm_ids: [],
    perm_cur_loc: [],
    perm_name: [],
  });
  
  const { id, roles } = useAuth();
  const user_current_office = useSelector((state) => state.office.off);

  // Check if user has Bo role for current office
  const hasBoRole = useMemo(() => {
    if (!roles || !user_current_office) return false;
    
    const currentOfficeRoles = roles?.[user_current_office] || [];
    const mainRoles = roles?.["main"] || [];
    const allUserRoles = [...mainRoles, ...currentOfficeRoles];
    
    return allUserRoles.includes("Bo");
  }, [roles, user_current_office]);

  // Memoize the partnos for API query
  const memoizedPartnos = useMemo(() => 
    selectedConsum?.map(x => x.partno),
    [selectedConsum]
  );

  // Should we skip the query?
  const shouldSkipQuery = useMemo(() => 
    selectedConsum?.length === 0 || !selectedConsum?.some(item => item.partno),
    [selectedConsum]
  );

  // API queries
  const { data: consumfinal, isLoading, isError } = useGetconsumfinalQuery({
    partnos: memoizedPartnos, 
    store: user_current_office 
  }, {
    skip: shouldSkipQuery,
    selectFromResult: ({ data, isLoading, isError }) => {
      // Transform normalized entities into an object indexed by partno for easy lookup
      const consumfinalByPartno = {};
      if (data?.entities) {
        Object.values(data.entities).forEach(item => {
          if (item?.partno) {
            consumfinalByPartno[item.partno] = item;
          }
        });
      }
      return {
        data: consumfinalByPartno,
        isLoading,
        isError
      };
    }
  });

  const { consumablemaster } = useGetconsummasterQuery('cosummaster', {
    selectFromResult: ({ data }) => ({
      consumablemaster: data?.entities
    }),
  });

  const { partnumber } = useGetpartnumberQuery('partno', {
    selectFromResult: ({ data }) => ({
      partnumber: data?.entities
    }),
  });

  const { modelnumber } = useGetModelNumbersQuery('modelnumber', {
    selectFromResult: ({ data }) => ({  
      modelnumber: data?.entities
    }),
  });

  const { storeoffice } = useGetStoreOfficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      storeoffice: data?.entities
    }),
  });

  // Memoize storeOffice to prevent recalculation on each render
  const storeOffice = useMemo(() => {
    return Object.values(storeoffice ?? {})?.find(x => x.storeoffice === storeSelectedValue) || {};
  }, [storeoffice, storeSelectedValue]);

  // Set filters when ID changes
  useEffect(() => {
    if (id) {
      setFilters({
        perm_alloc_ofc: [user_current_office],
        perm_cur_store: [storeSelectedValue],
        perm_alloc_user: [],
        perm_ids: [],
        perm_name: [],
      });
    }
  }, [id, user_current_office, storeSelectedValue]);

  // Helper functions
  const filteredPartNumbers = useCallback((assetName, brand, consumable) => {
    if (!modelnumber || !partnumber) return [];
  
    const modelNos = Object.values(modelnumber || {})
      .filter(m => m.asset === assetName && m.brand === brand)
      .map(m => m.modelno);
  
    return Object.values(partnumber || {}).filter(pn =>
      pn.consumable === consumable &&
      (
        pn.assetModelNos.includes(assetName) || // Exact asset name match
        pn.assetModelNos.some(model => modelNos.includes(model)) || // Model number match
        pn.assetModelNos.includes(`All-${assetName}`) || // "All-Asset" match
        pn.assetModelNos.includes(`All-${assetName}-${brand}`) // "All-Asset-Brand" match
      )
    ).map(pn => pn.partno);
  }, [modelnumber, partnumber]);  

  const handleSelectConsum = useCallback((event, newValue, i, name) => {
    setSelectedConsum(prevSelectedConsum => {
      let tempSelectedConsum = [...prevSelectedConsum];

      if (tempSelectedConsum[i]) {
        if (newValue === null) {
          switch (name) {
            case "consumable":
              tempSelectedConsum[i] = { ...tempSelectedConsum[i], consumable: null };
              return tempSelectedConsum;
        
            case "asset":
              tempSelectedConsum[i] = { ...tempSelectedConsum[i], asset: null, assettag: null, partno: null, quantity: "" };
              return tempSelectedConsum;
        
            case "assettag":
              tempSelectedConsum[i] = { ...tempSelectedConsum[i], assettag: null, partno: null, quantity: "" };
              return tempSelectedConsum;
        
            case "partno":
              tempSelectedConsum[i] = { ...tempSelectedConsum[i], partno: null, quantity: "" };
              return tempSelectedConsum;
        
            default:
              return tempSelectedConsum;
          }
        }

        // Update the object at index 'i' with the dynamic key 'name' and new value 'newValue'
        tempSelectedConsum[i] = event.target.name?.length 
          ? { 
              ...tempSelectedConsum[i], 
              [event.target.name]: event.target.value.length ? Number(event.target.value) : event.target.value 
            }
          : { 
              ...tempSelectedConsum[i], 
              [name]: newValue 
            };
      } else {
        // Ensure we push a new object at the correct index (if 'i' is greater than the current array length)
        while (tempSelectedConsum?.length <= i) {
          tempSelectedConsum.push({});
        }
        
        // Set the key 'name' with the value 'newValue' for the new object at index 'i'
        tempSelectedConsum[i] = event.target.name?.length
          ? { 
              [event.target.name]: event.target.value.length ? Number(event.target.value) : event.target.value 
            }
          : { 
              [name]: newValue 
            };
      }

      return tempSelectedConsum;
    });
  }, []);

  const onClickaddicon = useCallback(() => {
    setAddSpecIndex(prev => prev + 1);
  }, [setAddSpecIndex]);

  const onClickremoveicon = useCallback(() => {
    if (addSpecIndex > 1) {
      setAddSpecIndex(prev => prev - 1);
      setSelectedConsum(prev => {
        // Filter out the last element instead of using delete
        return prev.filter((_, index) => index !== addSpecIndex - 1);
      });
    }
  }, [addSpecIndex, setAddSpecIndex, setSelectedConsum]);

  // Memoize the quantity change handler to prevent recreation
  const handleQuantityChange = useCallback((event, newValue, i) => {
    // Only check quantity availability if part number is selected and consumfinal data is available
    if (event.target.value && 
        consumfinal && 
        selectedConsum[i]?.partno && 
        consumfinal?.[selectedConsum[i]?.partno]?.balance_order_quantity < parseInt(event.target.value, 10)) {
      alert("Sufficient Quantity not available");
      return;
    }
    handleSelectConsum(event, newValue, i, "quantity");
  }, [consumfinal, selectedConsum, handleSelectConsum]);

  // Memoize consumable options
  const consumableOptions = useMemo(() => {
    if (!consumablemaster) return [];
    
    const storeCategories = Array.isArray(storeOffice?.ApplConsumCateg) 
      ? storeOffice.ApplConsumCateg 
      : [];
      
    return Object.values(consumablemaster)
      ?.filter(obj => storeCategories.includes(obj.consumcategory))
      ?.map(elm => elm.consumable)
      .filter(x => !(selectedConsum?.map(y => y?.consumable) ?? []).includes(x));
  }, [consumablemaster, storeOffice, selectedConsum]);

  return (
    <>
      <FilteredAssetsFetcher filters={filters} onDataFetched={setAllData} />
    
      {[...Array(addSpecIndex)].map((e, i) => (
        <Addconsumformcont key={i}>
          {/* Consumable Selection */}
          <LabelInputWrapper>
            <Autocomplete
              id={`consumable-select-${i}`}
              size="small"
              options={consumableOptions}
              sx={{ width: 200 }}
              value={selectedConsum[i]?.["consumable"] || null}
              isOptionEqualToValue={(option, value) => option?.consumable === value?.consumable}
              onChange={(event, newValue) => handleSelectConsum(event, newValue, i, "consumable")}
              renderInput={(params) => <TextField {...params} label="Select Consumable" />}
            />
          </LabelInputWrapper>

          {/* Asset Selection - Only show if consumable is selected */}
          {selectedConsum[i]?.["consumable"]?.length && (
            <LabelInputWrapper>
              {(() => {
                const isAssetNotApplicable = consumablemaster && 
                  Object.values(consumablemaster)
                    ?.find(elm => elm.consumable === selectedConsum[i]?.["consumable"])
                    ?.asset?.includes("Not Applicable");
                
                const assetOptions = consumablemaster 
                  ? Object.values(consumablemaster)
                      ?.find(elm => elm.consumable === selectedConsum[i]?.["consumable"])
                      ?.asset || [] 
                  : [];
                
                return (
                  <Autocomplete
                    id={`asset-select-${i}`}
                    size="small"
                    disabled={isAssetNotApplicable}
                    options={assetOptions}
                    sx={{ width: 200 }}
                    value={isAssetNotApplicable ? "Not Applicable" : (selectedConsum[i]?.["asset"] || null)}
                    onChange={(event, newValue) => handleSelectConsum(event, newValue, i, "asset")}
                    renderInput={(params) => <TextField {...params} label="Select Asset" />}
                  />
                );
              })()}
            </LabelInputWrapper>
          )}

          {/* Asset Tag Selection - Only show if asset is selected or Not Applicable */}
          {(selectedConsum[i]?.["asset"]?.length || Object.values(consumablemaster || {})?.find(elm => elm.consumable === selectedConsum[i]?.["consumable"])?.asset?.includes("Not Applicable")) && (
            <LabelInputWrapper>
              {(() => {
                const isAssetNotApplicable = consumablemaster && selectedConsum[i] &&
                  Object.values(consumablemaster)
                    ?.find(elm => elm.consumable === selectedConsum[i]?.["consumable"])
                    ?.asset?.includes("Not Applicable");
                
                // Filter assets based on Bo role
                const assetTagOptions = allData && allData.assets
                  ? allData.assets
                    .filter(x => {
                      // First filter by asset name
                      if (x.name !== selectedConsum[i]?.["asset"]) return false;
                      
                      // If user has Bo role, show all assets for the office
                      if (hasBoRole) return true;
                      
                      // If user doesn't have Bo role, only show assets allocated to them
                      return x.alloc_user === id;
                    })
                    .map(x => ({ label: x.oem_tag, value: x }))
                  : [];
                  
                return (
                  <Autocomplete
                    id={`assettag-select-${i}`}
                    size="small"
                    disabled={isAssetNotApplicable}
                    options={assetTagOptions}
                    sx={{ width: 200 }}
                    value={
                      isAssetNotApplicable
                        ? { label: "Not Applicable", value: "Not Applicable" }
                        : selectedConsum[i]?.["assettag"] || null
                    }
                    onChange={(event, newValue) => handleSelectConsum(event, newValue, i, "assettag")}
                    isOptionEqualToValue={(option, value) => {
                      // Safely handle undefined or null values
                      return option?.value?.oem_tag === value?.value?.oem_tag;
                    }}
                    renderInput={(params) => <TextField {...params} label="Select Asset Tag" />}
                  />
                );
              })()}
            </LabelInputWrapper>
          )}

          {/* Part Number Selection - Only show if asset tag is selected or Not Applicable AND hidePartNumber is false */}
          {!hidePartNumber && (selectedConsum[i]?.["assettag"]?.label?.length || Object.values(consumablemaster || {})?.find(elm => elm.consumable === selectedConsum[i]?.["consumable"])?.asset?.includes("Not Applicable")) && (
            <LabelInputWrapper>
              {(() => {
                const isAssetNotApplicable = Object.values(consumablemaster || {})
                  ?.find(elm => elm.consumable === selectedConsum[i]?.["consumable"])
                  ?.asset?.includes("Not Applicable");
                
                const partNumberOptions = isAssetNotApplicable
                  ? Object.values(partnumber || {})
                      .filter(x => x.consumable === selectedConsum[i]?.["consumable"])
                      .map(x => x.partno)
                  : filteredPartNumbers(
                      selectedConsum[i]?.["assettag"]?.value?.name, 
                      selectedConsum[i]?.["assettag"]?.value?.oem, 
                      selectedConsum[i]?.["consumable"]
                    );
                
                return (
                  <Autocomplete
                    id={`partno-select-${i}`}
                    size="small"
                    options={partNumberOptions}
                    sx={{ width: 200 }}
                    value={selectedConsum[i]?.["partno"] || null}
                    onChange={(event, newValue) => handleSelectConsum(event, newValue, i, "partno")}
                    renderInput={(params) => <TextField {...params} label="Select Consum Part No." />}
                  />
                );
              })()}
            </LabelInputWrapper>
          )}

          {/* Quantity Selection - Show if part number is selected OR if hidePartNumber is true and asset tag is selected/not applicable */}
          {(selectedConsum[i]?.["partno"]?.length || 
            (hidePartNumber && (selectedConsum[i]?.["assettag"]?.label?.length || Object.values(consumablemaster || {})?.find(elm => elm.consumable === selectedConsum[i]?.["consumable"])?.asset?.includes("Not Applicable")))) && (
            <LabelInputWrapper>
              <div>
                <TextField
                  id={`quantity-input-${i}`}
                  size='small'
                  name={`quantity`}
                  onChange={(event, newValue) => handleQuantityChange(event, newValue, i)}
                  label="Quantity"
                  value={selectedConsum[i]?.["quantity"] || ""}
                  variant="outlined"
                  sx={{ width: 200 }}
                />
              </div>
            </LabelInputWrapper>
          )}

          {/* Add/Remove Icons */}
          {i === (addSpecIndex - 1) ? (
            <>
              {parseInt(selectedConsum[i]?.["quantity"] || 0, 10) > 0 && (
                <AddIcon onClick={onClickaddicon} sx={{ fontSize: 30 }} />
              )}
              {(i > 0) && <RemoveIcon onClick={onClickremoveicon} sx={{ fontSize: 30 }} />}
            </>
          ) : null}
        </Addconsumformcont>
      ))}
    </>
  );
};

export default React.memo(UnivConsumSelector);